import { consola } from 'consola'
import type { SlideAction } from '../model/types'
import { useCartStore } from '~/entities/order/@x/news-slider'
import { apiReferral } from '~/entities/referral/@x/news-slider'
import { useInstitutionStore } from '~/entities/institution/@x/news-slider'
import { useModalStore } from '@shared/model';
import { setProductOpenSource } from '@shared/lib';
import { pushToast, type HuiColor } from '@shared/ui/kit';

// Адаптер старых Nuxt UI цветов тоста → UI-kit тостер (снизу). red=ошибка, orange=предупреждение, green=ок.
const NUXT_TO_HUI: Record<string, HuiColor> = { red: 'danger', orange: 'warning', green: 'success' };
const toast = {
  add: (o: { title?: string; description?: string; color?: string; timeout?: number }) =>
    pushToast({ title: o.title, description: o.description, color: NUXT_TO_HUI[o.color ?? ''] ?? 'default', timeout: o.timeout }),
};

export const useSlideAction = () => {
  const router = useRouter();
  const cartStore = useCartStore();
  const institutionStore = useInstitutionStore();
  const modalStore = useModalStore();

  /**
   * Выполнить действие из слайда
   */
  const executeAction = async (action: SlideAction | null | undefined) => {
    if (!action || action.type === 'none') {
      consola.debug('[useSlideAction] No action to execute');
      return;
    }

    consola.debug('[useSlideAction] Executing action:', action);

    try {
      switch (action.type) {
        case 'external_link': {
          await handleExternalLink(action.data);
          break;
        }
        
        case 'open_product': {
          await handleOpenProduct(action.data);
          break;
        }
        
        case 'copy_promo': {
          await handleCopyPromo(action.data);
          break;
        }
        
        case 'navigate': {
          await handleNavigate(action.data);
          break;
        }
        
        case 'share_referral': {
          await handleShareReferral(action.data);
          break;
        }
        
        default: {
          consola.warn('[useSlideAction] Unknown action type:', action.type);
        }
      }
    } catch (error) {
      consola.error('[useSlideAction] Error executing action:', error);
      toast.add({
        title: 'Ошибка',
        description: 'Не удалось выполнить действие',
        color: 'red',
      });
    }
  };

  /**
   * 1. Открыть внешнюю ссылку
   */
  const handleExternalLink = (data: any) => {
    const { url, openInNewTab = true } = data;
    
    if (!url) {
      throw new Error('URL is required for external_link action');
    }

    if (process.client) {
      if (openInNewTab) {
        window.open(url, '_blank');
      } else {
        window.location.href = url;
      }
    }
  };

  /**
   * 2. Открыть товар в модальном окне
   */
  const handleOpenProduct = (data: any) => {
    const { productId } = data;
    
    if (!productId) {
      throw new Error('productId is required for open_product action');
    }

    try {
      // Ищем товар в store (товары уже загружены)
      const result = findProductById(productId);
      
      consola.debug('[useSlideAction] findProductById result:', {
        productId,
        found: !!result.product,
        isVariant: result.isVariant,
        hasParent: !!result.parentProduct,
        productName: result.product?.name,
        parentName: result.parentProduct?.name,
      });
      
      if (!result.product) {
        toast.add({
          title: 'Товар не найден',
          description: `Товар ID ${productId} не найден в каталоге`,
          color: 'orange',
        });
        return;
      }

      // Если это вариант группового товара, передаем родительский товар и ID варианта
      const productToOpen = result.isVariant && result.parentProduct 
        ? result.parentProduct 
        : result.product;
      
      // ID варианта для открытия нужного таба (или null для обычного товара)
      const variantIdToOpen = result.isVariant ? productId : null;

      consola.debug('[useSlideAction] Opening product:', {
        productToOpenId: (productToOpen as any).id,
        productToOpenName: (productToOpen as any).name,
        productToOpenType: (productToOpen as any).type,
        variantIdToOpen,
        variants: (productToOpen as any).variants?.map((v: any) => ({ id: v.id, name: v.name })),
      });

      // Устанавливаем выбранное блюдо в store с указанием предпочитаемого варианта
      cartStore.setSelectedDish(productToOpen, variantIdToOpen);
      
      consola.debug('[useSlideAction] After setSelectedDish, cartStore.preferredVariantId:', cartStore.preferredVariantId);
      
      // Открываем модальное окно
      setProductOpenSource('news_slider');
      modalStore.open('MoreAboutDish');
    } catch (error) {
      consola.error('[useSlideAction] Error loading product:', error);
      toast.add({
        title: 'Ошибка',
        description: 'Не удалось открыть товар',
        color: 'red',
      });
    }
  };

  /**
   * 3. Скопировать промокод
   */
  const handleCopyPromo = async (data: any) => {
    const { code, successMessage = 'Промокод скопирован!' } = data;
    
    if (!code) {
      throw new Error('code is required for copy_promo action');
    }

    if (process.client) {
      try {
        await navigator.clipboard.writeText(code);
        
        toast.add({
          title: successMessage,
          description: `Промокод: ${code}`,
          color: 'green',
          timeout: 3000,
        });
      } catch {
        // Fallback для старых браузеров
        const textArea = document.createElement('textarea');
        textArea.value = code;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.append(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          toast.add({
            title: successMessage,
            description: `Промокод: ${code}`,
            color: 'green',
            timeout: 3000,
          });
        } catch (error_) {
          consola.error('Fallback copy failed:', error_);
          toast.add({
            title: 'Ошибка',
            description: 'Не удалось скопировать промокод',
            color: 'red',
          });
        } finally {
          textArea.remove();
        }
      }
    }
  };

  /**
   * 4. Навигация внутри приложения
   */
  const handleNavigate = async (data: any) => {
    const { route, params = {} } = data;
    
    if (!route) {
      throw new Error('route is required for navigate action');
    }

    await router.push({
      path: route,
      query: params,
    });
  };

  /**
   * 5. Поделиться реферальной ссылкой
   */
  const handleShareReferral = async (data: any) => {
    const { message = 'Попробуй это приложение!' } = data;
    
    if (process.client) {
      try {
        // Получаем реферальную ссылку пользователя
        const referralLink = await getReferralLink();
        
        const shareData = {
          title: 'Приглашение',
          text: message,
          url: referralLink,
        };

        // Проверяем поддержку Web Share API
        if (navigator.share) {
          await navigator.share(shareData);
          
          toast.add({
            title: 'Успешно',
            description: 'Спасибо за приглашение друга!',
            color: 'green',
          });
        } else {
          // Fallback: копируем ссылку в буфер обмена
          await navigator.clipboard.writeText(referralLink);
          
          toast.add({
            title: 'Ссылка скопирована',
            description: 'Реферальная ссылка скопирована в буфер обмена',
            color: 'green',
            timeout: 3000,
          });
        }
      } catch (error) {
        consola.error('[useSlideAction] Error sharing referral:', error);
        
        if ((error as Error).name !== 'AbortError') {
          toast.add({
            title: 'Ошибка',
            description: 'Не удалось поделиться ссылкой',
            color: 'red',
          });
        }
      }
    }
  };

  /**
   * Найти товар по ID в store (helper функция)
   * Возвращает объект с информацией о товаре и его родителе (если это вариант)
   */
  /* eslint-disable sonarjs/cognitive-complexity -- обход каталога и вариантов групповых товаров */
  const findProductById = (productId: string | number): {
    product: any | null;
    isVariant: boolean;
    parentProduct: any | null;
  } => {
    consola.debug('[findProductById] Searching for productId:', productId, 'type:', typeof productId);
    
    // Ищем в institutionStore.products (массив категорий с товарами)
    if (institutionStore.products && Array.isArray(institutionStore.products)) {
      for (const category of institutionStore.products) {
        if (category.products && Array.isArray(category.products)) {
          // Ищем напрямую в товарах категории
          const found = category.products.find((p: any) => 
            String(p.id) === String(productId),
          );
          
          if (found) {
            consola.debug('[findProductById] Found direct product:', found.name, 'id:', found.id);
            return { product: found, isVariant: false, parentProduct: null };
          }
          
          // Ищем в variants групповых товаров
          for (const product of category.products) {
            const productAny = product as any;
            if (productAny.type === 'group' && productAny.variants && Array.isArray(productAny.variants)) {
              const variantFound = productAny.variants.find((v: any) => 
                String(v.id) === String(productId),
              );
              
              if (variantFound) {
                consola.debug('[findProductById] Found variant:', variantFound.name, 'id:', variantFound.id, 'in parent:', productAny.name, 'parentId:', productAny.id);
                // Возвращаем родительский групповой товар, чтобы показать все варианты
                return { 
                  product: variantFound, 
                  isVariant: true, 
                  parentProduct: product,
                };
              }
            }
          }
        }
      }
    }
    
    // Пробуем найти в избранном
    if (institutionStore.favorites && Array.isArray(institutionStore.favorites)) {
      for (const category of institutionStore.favorites) {
        if (category.products && Array.isArray(category.products)) {
          const found = category.products.find((p: any) => 
            String(p.id) === String(productId),
          );
          if (found) {
            return { product: found, isVariant: false, parentProduct: null };
          }
          
          // Ищем в variants групповых товаров в избранном
          for (const product of category.products) {
            const productAny = product as any;
            if (productAny.type === 'group' && productAny.variants && Array.isArray(productAny.variants)) {
              const variantFound = productAny.variants.find((v: any) => 
                String(v.id) === String(productId),
              );
              if (variantFound) {
                return { 
                  product: variantFound, 
                  isVariant: true, 
                  parentProduct: product,
                };
              }
            }
          }
        }
      }
    }
    
    return { product: null, isVariant: false, parentProduct: null };
  };
  /* eslint-enable sonarjs/cognitive-complexity */

  /**
   * Личная реферальная ссылка пользователя.
   *
   * Раньше здесь стоял TODO и возвращался просто origin сайта: гость «делился
   * приглашением», в котором не было его реферального кода, — привести друга по такой
   * ссылке невозможно. Бэкенд отдаёт готовые ссылки в `/referral/my-code`
   * (universalLink → referralUrl), берём их.
   *
   * Ссылки может не быть: программа выключена или не набрано нужное число покупок —
   * тогда бэкенд отвечает success: false с человеческим текстом. В этом случае делиться
   * нечем, и об этом честнее сказать, чем копировать бесполезный адрес.
   */
  const getReferralLink = async (): Promise<string> => {
    const response: any = await apiReferral.getMyReferralCode();
    const link = response?.universalLink || response?.referralUrl;

    if (!link) {
      throw new Error(response?.message || 'Реферальная ссылка пока недоступна');
    }

    return link;
  };

  return {
    executeAction,
  };
};

